import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CountdownToLaunch } from '../components/CountdownToLaunch';
import {
    Droplets, ExternalLink, ArrowRight, Wallet, Shield, TrendingUp,
    Coins, AlertTriangle, Check, BookOpen, Lock,
} from 'lucide-react';

const STEPS = [
    { key: 'connect', icon: Wallet },
    { key: 'getEth', icon: Coins },
    { key: 'addLiquidity', icon: Droplets },
    { key: 'stake', icon: Lock },
    { key: 'collect', icon: TrendingUp },
];

const RISKS = ['impermanent', 'volatility', 'smartContract'];

export default function DexGuidePage() {
    const { t } = useTranslation();
    return (
        <div className="min-h-screen" data-testid="dex-guide-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0066CC]/15 via-transparent to-[#FFD700]/10" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0066CC]/15 border border-[#0066CC]/30 mb-6">
                        <Droplets className="w-3.5 h-3.5 text-[#6FA8FF]" />
                        <span className="text-[#6FA8FF] text-xs font-bold uppercase tracking-widest">
                            {t('dexGuide.heroBadge')}
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-3">
                        {t('dexGuide.heroTitle1')} <span className="text-[#FFD700]">Aerodrome</span>
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
                        {t('dexGuide.heroDesc')}
                    </p>
                    <CountdownToLaunch compact />
                </div>
            </section>

            {/* WHY ADD LIQUIDITY */}
            <section className="py-12 border-b border-white/10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-black text-white text-center mb-2">
                        {t('dexGuide.whyTitle')}
                    </h2>
                    <p className="text-white/60 text-center max-w-2xl mx-auto mb-10 text-sm">
                        {t('dexGuide.whyDesc')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-white/[0.04] border-white/10">
                            <CardContent className="p-5">
                                <Coins className="w-7 h-7 text-[#FFD700] mb-3" />
                                <h3 className="font-bold text-white mb-2">{t('dexGuide.whyFee')}</h3>
                                <p className="text-sm text-white/60">{t('dexGuide.whyFeeDesc')}</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10">
                            <CardContent className="p-5">
                                <TrendingUp className="w-7 h-7 text-[#00FF66] mb-3" />
                                <h3 className="font-bold text-white mb-2">{t('dexGuide.whyAero')}</h3>
                                <p className="text-sm text-white/60">{t('dexGuide.whyAeroDesc')}</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10">
                            <CardContent className="p-5">
                                <Shield className="w-7 h-7 text-[#9B6FE8] mb-3" />
                                <h3 className="font-bold text-white mb-2">{t('dexGuide.whyProtocol')}</h3>
                                <p className="text-sm text-white/60">{t('dexGuide.whyProtocolDesc')}</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* STEP BY STEP */}
            <section className="py-12 border-b border-white/10 bg-gradient-to-br from-[#001A4D]/30 to-transparent">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-black text-white text-center mb-10">
                        {t('dexGuide.stepsTitle')}
                    </h2>
                    <div className="space-y-4">
                        {STEPS.map((s, idx) => {
                            const Icon = s.icon;
                            return (
                                <Card key={s.key} className="bg-white/[0.04] border-white/10" data-testid={`dex-step-${s.key}`}>
                                    <CardContent className="p-5 flex items-start gap-4">
                                        <div className="flex flex-col items-center gap-2 shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 flex items-center justify-center font-black text-[#FFD700]">
                                                {idx + 1}
                                            </div>
                                            <Icon className="w-5 h-5 text-[#6FA8FF]" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-bold text-white mb-1">{t(`dexGuide.step_${s.key}`)}</h3>
                                            <p className="text-sm text-white/70 leading-relaxed">{t(`dexGuide.step_${s.key}_desc`)}</p>
                                            {s.key === 'addLiquidity' && (
                                                <a
                                                    href="https://aerodrome.finance/liquidity"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 mt-3 text-[#FFD700] hover:text-[#FFC700] text-sm font-semibold"
                                                    data-testid="aero-liquidity-link"
                                                >
                                                    {t('dexGuide.openAerodrome')}
                                                    <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* RISKS */}
            <section className="py-12 border-b border-white/10">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-black text-white text-center mb-6">
                        <AlertTriangle className="w-7 h-7 text-[#FF6B35] inline mr-2 -mt-1" />
                        {t('dexGuide.risksTitle')}
                    </h2>
                    <p className="text-white/60 text-center mb-8 text-sm">{t('dexGuide.risksDesc')}</p>
                    <div className="space-y-3">
                        {RISKS.map((r) => (
                            <Card key={r} className="bg-[#FF6B35]/5 border-[#FF6B35]/20">
                                <CardContent className="p-4 flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-[#FF6B35] shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-bold text-white mb-1">{t(`dexGuide.risk_${r}`)}</h4>
                                        <p className="text-sm text-white/70">{t(`dexGuide.risk_${r}_desc`)}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-14 bg-gradient-to-br from-[#FFD700]/5 to-transparent">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <Check className="w-10 h-10 text-[#00FF66] mx-auto mb-3" />
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">{t('dexGuide.ctaTitle')}</h2>
                    <p className="text-white/60 mb-8">{t('dexGuide.ctaDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <a href="https://aerodrome.finance/liquidity?token0=ETH&token1=B4P" target="_blank" rel="noopener noreferrer">
                            <Button size="lg" className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-8 py-6" data-testid="cta-open-aerodrome">
                                <Droplets className="w-4 h-4 mr-2" />
                                {t('dexGuide.openAerodrome')}
                                <ExternalLink className="w-3 h-3 ml-2" />
                            </Button>
                        </a>
                        <Link to="/b4p">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6">
                                <BookOpen className="w-4 h-4 mr-2" />
                                {t('dexGuide.viewB4p')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
